import React, { useEffect, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAuthContext } from '@retail/auth/data-access';
import { BodyLoader } from '@retail/components';
import { AppTenant, Auth0Token, MGNamespace } from '@retail/auth/types';
import { LandingPage } from '../Landing';
import { PublicErrorPage } from '../PublicError';
import { NotAuthorizedPage } from '../NotAuthorized';
import * as Sentry from '@sentry/react';

const getAppTenants = (accessToken: string): AppTenant[] => {
  const decoded = jwtDecode<Auth0Token>(accessToken);
  const tokenMeta = decoded[MGNamespace];
  const tenants: AppTenant[] = [];

  if (tokenMeta.apps.includes(AppTenant.NORWAY.tenantAppKey)) tenants.push(AppTenant.NORWAY);
  if (tokenMeta.apps.includes(AppTenant.SWEDEN.tenantAppKey)) tenants.push(AppTenant.SWEDEN);

  return tenants;
};

interface Props {
  children: (props: { appTenants: AppTenant[] }) => React.ReactNode;
}

export function AuthContainerPage({ children }: Props) {
  const { isAuthenticated, isLoading: isAuth0Loading, token, error } = useAuthContext();

  const appTenants = useMemo(() => (token ? getAppTenants(token) : undefined), [token]);

  useEffect(() => {
    if (token) {
      const decoded = jwtDecode<Auth0Token>(token);
      Sentry.setUser({ id: decoded.sub });
    }
  }, [token]);

  if (error) {
    return <PublicErrorPage />;
  }

  if (appTenants) {
    if (appTenants.length === 0) {
      return <NotAuthorizedPage reason="noAppAccess" />;
    }
    return <>{children({ appTenants })}</>;
  }

  if (!isAuthenticated && !isAuth0Loading) {
    return <LandingPage />;
  }

  return <BodyLoader />;
}
